import { CreateDco } from '../create/create-dco';
import { Store } from '@ngrx/store';
import { State } from '@cemex/cmx-client-layout';
import { getRmsRecipeSpecifications } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators';
import { getOpenResetWarningModal, getSlotForFormID, getSnapShotPod } from '@ordersnproducts/modules/shared/reducers';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { COUNTRY_CODE, OrderStatusEnum } from '@cemex-core/cmx-api';
import { OrderStatusModify } from '@ordersnproducts/modules/shared/models/dco/order-status-modify-snapshot.model';
import { generateAddedValueFormName } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/form-added-value-name-generator';
import * as fromDcoActions from '@ordersnproducts/modules/shared/actions/dco.actions';
import {
  getOrderModifyFormsTopLocationState,
  getOrderSelectedDetail as getOrderSelectedDetailModify,
} from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { ModifyOrderColorSpecialScenarios } from '@ordersnproducts/modules/shared/actions/dco.actions';
import { getCutOffTemperatureState } from '../../../../../order-builder/modules/rms-specification/selectors/cut-off.selectors';
import * as moment from 'moment';
import { _safe } from '../../../../utils/safe-get.method';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import {
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_QUANTITY,
} from 'app/shared/pumping/pumping.constant';
import { getPumpPrimerState } from '@ordersnproducts/modules/shared/modules/pump-primer/redux/pump-primer.selectors';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';

export class ModifyDco extends CreateDco {
  public modifyReceptorActive = true;

  /**
   * Flag to know if we should use the new modification rules for orders
   */
  public useNewOrderModificationsRules = false;

  constructor(
    protected store: Store<State>,
    public featureToggle: FeatureToggleService,
    /**
     * Subject to notify should ignore changes on DCO fields
     */
    private readonly ignoreDcoChangesNotification$: BehaviorSubject<boolean>,
    /**
     * Subject to notify should reset DCO by warning modal
     */
    private readonly resetDcoByWarningModal$: Subject<number>,
    /**
     * Flag used to let UnloadTypeOnChangeService know that its function got triggered
     * by pressing the Cancel button on the warning modal.
     * This is to avoid the modal showing up again after closing it, caused by the
     * pump primer fields resetting.
     */
    protected didCloseWarningModal$: BehaviorSubject<boolean>,
    /**
     * For new waiting list flow we need to validate volume on edge cases like decreasing from BIGO
     */
    private readonly isNewWaitingList: boolean,
  ) {
    super(store, featureToggle, didCloseWarningModal$);
  }
  onInit() {
    this.useNewOrderModificationsRules = this.featureToggle.isFeatureEnabled(Features.IMPROVEMENTS_MODIFY_ORDER_RULES);
    this.isModalOpen$ = new BehaviorSubject({
      specField: null,
      isOpen: this.openResetWarningModal,
    });
    this.monitorRecipeChangesForReset();
    this.validFieldsNames();
    if (this.featureToggle.isFeatureEnabled(Features.EnablePODDropdownFeature)) {
      this.monitorPointOfDelivery();
      this.monitorSnapShotPod();
    }
    if (this.featureToggle.isFeatureEnabled(Features.EnablePODCreateOnModifyOrderFeature)) {
      this.monitorOpenResetWarningModal();
    }
  }
  monitorSnapShotPod() {
    this.store
      .select(getSnapShotPod)
      .pipe(
        takeUntil(this.ngDestroy),
        map((snapshot) => {
          this.oldPod = _safe(() => snapshot.shipmentLocationId);
        }),
      )
      .subscribe();
  }
  public monitorRecipeChangesForReset() {
    this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        debounceTime(300),
        takeUntil(this.ngDestroy),
        filter(
          (recipes) =>
            recipes.find((recipe) => recipe.id === this.formSelector.formId && this.formSelector.formIndex === 0) !=
            null,
        ),
        map((recipes) =>
          recipes.find((recipe) => recipe.id === this.formSelector.formId && this.formSelector.formIndex === 0),
        ),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap((recipe) => {
          return this.store.select(getSlotForFormID(this.formSelector.formId)).pipe(
            filter((slot) => slot && slot.formSnapshot != null),
            map((slot) => {
              return { recipe, slot };
            }),
            take(1),
          );
        }),
        filter((stateSnapshot) => !!stateSnapshot.recipe && !!stateSnapshot.slot),
        switchMap((stateSnapshot) => {
          const { recipe, slot } = stateSnapshot;
          return this.store.select(getPumpPrimerState).pipe(
            take(1),
            map(({ unloadTypePumpValues }) => ({ unloadTypePumpValues, recipe, slot })),
          );
        }),
        switchMap(({ unloadTypePumpValues, recipe, slot }) => {
          return this.store.select(getRmsRecipeSpecifications).pipe(
            take(1),
            map((recipes) => ({ recipes, unloadTypePumpValues, recipe, slot })),
            withLatestFrom(this.ignoreDcoChangesNotification$),
          );
        }),
        map(([{ recipes, unloadTypePumpValues, recipe, slot }, isIgnoreChanges]) => {
          const countryCode = this.country.getCountryCodeProperty();
          let isSameValuePumpPrimerMix = true;
          let specField = null;
          if (recipes.indexOf(recipe) > 0) {
            this.openResetWarningModal = false;
            this.isModalOpen$.next({
              ...this.isModalOpen$.value,
              isOpen: this.openResetWarningModal,
            });
            this.resetDcoByWarningModal$.next(recipe.id);
            return EMPTY;
          }
          if (isIgnoreChanges) {
            this.ignoreDcoChangesNotification$.next(false);
            return EMPTY;
          }
          const valuesAreSame = this.fieldNames.every((name) => {
            if (name === SpecFieldNames.QUANTITY) {
              if (recipe.values[name] > slot.formSnapshot.values[name]) {
                this.quantityWasIncrement = true;
              } else {
                this.quantityWasIncrement = false;
              }
            }
            if (name === SpecFieldNames.PRODUCT_SELECTION && recipe.values[name] !== slot.formSnapshot.values[name]) {
              specField = SpecFieldNames.PRODUCT_SELECTION;
            }

            if (recipe.values[name] === slot.formSnapshot.values[name]) {
              return true;
            } else if (name === SpecFieldNames.QUANTITY && recipe.values[name] < slot.formSnapshot.values[name]) {
              return this.validateDecreaseQuantity(slot.requestDatetimeIsAvailable);
            } else if (name === SpecFieldNames.DISCHARGE_TIME && !this.useNewOrderModificationsRules) {
              return false;
            } else if (
              name === SpecFieldNames.DISCHARGE_TIME &&
              this.useNewOrderModificationsRules &&
              recipe.values[name] < slot.formSnapshot.values[name]
            ) {
              return true;
            } else if (name === SpecFieldNames.ADJUSTMENT && !this.useNewOrderModificationsRules) {
              return true;
            } else if (
              name === SpecFieldNames.ADJUSTMENT &&
              this.useNewOrderModificationsRules &&
              slot.formSnapshot.values[name] === true &&
              recipe.values[name] !== slot.formSnapshot.values[name]
            ) {
              return true;
            } else if (
              name === SpecFieldNames.COLOR_INFO ||
              (name === SpecFieldNames.ADDITIONAL_INFO && recipe.values[name] !== slot.formSnapshot.values[name])
            ) {
              return this.validateColorScenearios();
            } else if (name === SpecFieldNames.ICE_QUANTITY && recipe.values[name] !== slot.formSnapshot.values[name]) {
              this.validateIceQuantity(
                recipe.values[name],
                slot.formSnapshot.values[name],
                recipe.values.date,
                slot.requestDatetimeIsAvailable,
              );
              return true;
            } else if (name === SpecFieldNames.TEMPERATURE_UNDER) {
              return this.resetAvailabilityTemperatureUnder(recipe.values[name], slot.formSnapshot.values[name]);
            } else if (name === SpecFieldNames.UNLOAD_TYPE && recipe.values[name] !== slot.formSnapshot.values[name]) {
              return this.handlerUnloadTypeFormByCountry(
                countryCode,
                recipe.values[name],
                slot.formSnapshot.values[name],
                unloadTypePumpValues,
              );
            } else if (name === PUMP_PRIMER_MIX) {
              isSameValuePumpPrimerMix = this.isSameValuePumpPrimerMix(
                recipe.values[name],
                slot.formSnapshot.values[name],
              );
              return isSameValuePumpPrimerMix;
            } else if (name === SpecFieldNames.SUMMER_SET) {
              return recipe.values[name] === slot.formSnapshot.values[name];
            }
            return false;
          });
          if (!isSameValuePumpPrimerMix && recipes.length > 1) {
            // Avoid display reset warning modal when pump primer mix was changed and there is more than 1 product
            // This is because also will be displayed modal refers to delete additional products when is
            // selecting primer mix in first product
            return EMPTY;
          } else if (!valuesAreSame) {
            if (!this.wasWarningModalOpened && this.useNewOrderModificationsRules) {
              this.confirmResetDCO = false;
              this.emitOpenResetWarningModal(specField);
              this.wasWarningModalOpened = true; // Set the flag to true after opening the modal once
            } else if (!this.useNewOrderModificationsRules) {
              this.confirmResetDCO = false;
              this.emitOpenResetWarningModal(specField);
            }
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private validateDecreaseQuantity(isSlotAvailable: boolean) {
    this.country.getCountryCode();
    const countryCode = this.country.getCountryCodeProperty();
    if (countryCode === COUNTRY_CODE.USA && this.orderStatus.originalStatus.statusCode === OrderStatusEnum.Confirmed) {
      if (!isSlotAvailable && this.isNewWaitingList) {
        return false;
      }
      return true && !this.dcoCheckedForQuantityChange;
    } else if (
      countryCode === COUNTRY_CODE.USA &&
      (this.orderStatus.originalStatus.statusCode === OrderStatusEnum.ToBeConfirmedOnline ||
        this.orderStatus.originalStatus.statusCode === OrderStatusEnum.InReview)
    ) {
      return false;
    } else if (
      this.useNewOrderModificationsRules &&
      this.orderStatus.originalStatus.statusCode === OrderStatusEnum.Confirmed
    ) {
      return true;
    }
    return false;
  }

  public orderStatusModify() {
    // Remove order status
    this.store.dispatch(new fromDcoActions.DeleteModifyOrderStatus());
    // Save order status only if is modify flow
    let isDone = false;
    const validateOrderStatus$ = this.store.select(getOrderSelectedDetailModify).pipe(
      filter((orderDetail) => !!orderDetail),
      map((orderDetail) => {
        this.orderStatus = {
          originalStatus: orderDetail.status,
          haveChanges: false,
        } as OrderStatusModify;
        this.modifyReviewStatus = orderDetail.status.statusCode === OrderStatusEnum.InReview ? true : false;
        this.store.dispatch(new fromDcoActions.SetOriginalOrderStatus(this.orderStatus));
        isDone = true;
      }),
      takeWhile(() => isDone === false),
    );
    validateOrderStatus$.subscribe();
  }

  validFieldsNames() {
    if (this.isUsaAndConfirmedOrReviewOrWillCall()) {
      return (this.fieldNames = [
        SpecFieldNames.QUANTITY,
        SpecFieldNames.PRODUCT_SELECTION,
        SpecFieldNames.CONTRACT_SELECT,
        SpecFieldNames.LOAD_SIZE,
        SpecFieldNames.DISCHARGE_TIME,
        SpecFieldNames.SPACING,
        SpecFieldNames.SUMMER_SET,
        generateAddedValueFormName(0),
        generateAddedValueFormName(1),
        generateAddedValueFormName(2),
        generateAddedValueFormName(3),
        SpecFieldNames.ADDITIONAL_INFO,
        SpecFieldNames.COLOR_INFO,
        SpecFieldNames.ICE_QUANTITY,
        SpecFieldNames.TEMPERATURE_UNDER,
        SpecFieldNames.POINT_OF_DELIVERY,
        SpecFieldNames.UNLOAD_TYPE,
        PUMP_PRIMER_MIX,
        PUMP_PRIMER_QUANTITY,
        PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
      ]);
    }
    return this.fieldNames;
  }

  validateColorScenearios() {
    if (this.isUsaAndConfirmedOrReviewOrWillCall()) {
      this.store.dispatch(new ModifyOrderColorSpecialScenarios(true));
      return true;
    }
    return false;
  }
  public validateChangeDateTime(
    slotsApiRequestedDateTimeSnapshot: moment.Moment,
    requestDatetimeMoment: moment.Moment,
  ): boolean {
    if (
      slotsApiRequestedDateTimeSnapshot != null &&
      slotsApiRequestedDateTimeSnapshot.isSame(requestDatetimeMoment, 'minute') === false
    ) {
      return true;
    }
    return false;
  }

  public isUsaAndConfirmedOrReviewOrWillCall() {
    this.country.getCountryCode();
    const countryCode = this.country.getCountryCodeProperty();
    return countryCode === COUNTRY_CODE.USA && this.isConfirmedOrReviewOrWillCall;
  }

  private validateIceQuantity(newValue, oldValue, recipeDate, isConfirmed) {
    if (
      (!!newValue && (oldValue === undefined || oldValue === null)) ||
      ((newValue === undefined || newValue === null) && !!oldValue)
    ) {
      this.confirmResetDCO = false;
      this.emitOpenResetWarningModal();
    }

    if (!!newValue && !!oldValue && newValue !== oldValue) {
      const validateCutOff$ = this.store.select(getCutOffTemperatureState).pipe(
        take(1),
        map((cutoff) => {
          if (isConfirmed && newValue === 'NONE') {
            this.confirmResetDCO = false;
            this.emitOpenResetWarningModal();
          } else if (isConfirmed) {
            const orderDate = moment(recipeDate.format('DD-MM-YYYY HH:mm'));
            const cutoffDate = _safe(() => moment(cutoff.cutOff.dateAvailable), undefined);
            const isBefore = _safe(() => orderDate.isBefore(cutoffDate), false);
            if (isBefore && cutoff.isTemperatureControl) {
              this.confirmResetDCO = false;
              this.emitOpenResetWarningModal();
            }
          }
          if (!isConfirmed) {
            this.confirmResetDCO = false;
            this.emitOpenResetWarningModal();
          }
        }),
      );
      validateCutOff$.subscribe();
    }
  }

  get isConfirmedOrReviewOrWillCall(): boolean {
    const isConfirmed = this.orderStatus.originalStatus.statusCode === OrderStatusEnum.Confirmed;
    const isReview = this.orderStatus.originalStatus.statusCode === OrderStatusEnum.InReview;
    const isWillCall = this.orderStatus.originalStatus.statusCode === OrderStatusEnum.ToBeConfirmedOnline;
    return isConfirmed || isReview || isWillCall;
  }

  monitorPointOfDelivery() {
    this.store
      .select(getOrderModifyFormsTopLocationState)
      .pipe(
        takeUntil(this.ngDestroy),
        filter((topLocationStateForm) => !!topLocationStateForm[0] && !!topLocationStateForm[0].values),
        withLatestFrom(this.store.select(getSlotForFormID(this.formSelector.formId))),
        map(([topLocationStateForm, slot]) => {
          if (
            this.oldPod &&
            this.oldPod !== topLocationStateForm[0].values.pointOfDelivery &&
            slot?.formSnapshot != null
          ) {
            this.emitOpenResetWarningModal();
          }
        }),
      )
      .subscribe();
  }

  private monitorOpenResetWarningModal() {
    this.store
      .select(getOpenResetWarningModal)
      .pipe(takeUntil(this.ngDestroy))
      .subscribe((openResetWarningModal) => {
        if (!this.openResetWarningModal && openResetWarningModal) {
          this.emitOpenResetWarningModal();
        }
      });
  }

  private emitOpenResetWarningModal(specField?: SpecFieldNames) {
    this.openResetWarningModal = true;
    this.isModalOpen$.next({
      ...(specField ? { specField } : this.isModalOpen$.value),
      isOpen: this.openResetWarningModal,
    });
  }

  /**
   * Function to compare the values of the unloadType to show the DCO modal
   * depending by country
   * @param countryCode string
   * @param newValue string
   * @param oldValue string
   * @param unloadTypePumpValues string[]
   * @returns boolean
   */
  private handlerUnloadTypeFormByCountry(
    countryCode: string,
    newValue: string,
    oldValue: string,
    unloadTypePumpValues: string[],
  ): boolean {
    if (countryCode === COUNTRY_CODE.MEXICO) {
      return this.markAsValidPumpingAsUnloadType(newValue, oldValue);
    }
    return this.isSameValueUnloadType(newValue, oldValue, unloadTypePumpValues);
  }
}
